import React, { useEffect, useRef, useState } from 'react'
import { Box, Eye, EyeOff, Pencil, Tag } from 'lucide-react'
import { AppObject } from '../types'

type SelectMode = 'replace' | 'toggle' | 'range'

interface ObjectTreeProps {
  objects: AppObject[]
  selectedIds: string[]
  onSelect: (id: string, mode: SelectMode) => void
  onRename: (id: string, newName: string) => void
  onDelete: (id: string) => void
  onToggleHide: (id: string) => void
  onSplit: (id: string) => void
  /** Cut a part in two at a height — for a body finished in two colours. */
  onSplitByHeight: (id: string) => void
  onJoin: (ids: string[]) => void
  /**
   * Open the part menu at a screen position on behalf of another surface — the 3D view
   * right-clicks through this, so both places offer the same actions.
   */
  menuRequest?: { id: string; x: number; y: number } | null
  onMenuRequestHandled?: () => void
}

interface ContextMenu {
  id: string
  x: number
  y: number
}

/**
 * Outliner: every part in the model, kept in sync with the 3D selection both ways.
 * Rename inline (double-click the name or the pencil), right-click for the full menu —
 * the same menu the 3D view opens.
 */
export const ObjectTree: React.FC<ObjectTreeProps> = ({
  objects,
  selectedIds,
  onSelect,
  onRename,
  onDelete,
  onToggleHide,
  onSplit,
  onSplitByHeight,
  onJoin,
  menuRequest,
  onMenuRequestHandled,
}) => {
  const selectedSet = new Set(selectedIds)
  const primaryId = selectedIds.length ? selectedIds[selectedIds.length - 1] : null

  const modeFromEvent = (e: React.MouseEvent): SelectMode =>
    e.ctrlKey || e.metaKey ? 'toggle' : e.shiftKey ? 'range' : 'replace'
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [menu, setMenu] = useState<ContextMenu | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const selectedRowRef = useRef<HTMLDivElement>(null)

  // A right-click in the 3D view opens this same menu.
  useEffect(() => {
    if (!menuRequest) return
    setMenu(menuRequest)
    onMenuRequestHandled?.()
  }, [menuRequest, onMenuRequestHandled])

  // Keep the active row visible when selection is driven from the 3D view.
  useEffect(() => {
    if (editingId) return
    selectedRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [primaryId, editingId])

  // Focus the input when entering edit mode.
  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingId])

  // Dismiss the context menu on any outside click, scroll, or Escape.
  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(null)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null)
    }
    window.addEventListener('click', close)
    window.addEventListener('contextmenu', close)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('click', close)
      window.removeEventListener('contextmenu', close)
      window.removeEventListener('keydown', onKey)
    }
  }, [menu])

  const startEdit = (obj: AppObject) => {
    setEditingId(obj.id)
    setDraft(obj.id)
  }

  const commitEdit = () => {
    if (editingId) {
      const trimmed = draft.trim()
      if (trimmed && trimmed !== editingId) onRename(editingId, trimmed)
    }
    setEditingId(null)
  }

  const openMenu = (e: React.MouseEvent, obj: AppObject) => {
    e.preventDefault()
    e.stopPropagation()
    // Preserve a multi-selection when right-clicking one of its members (so "Join"
    // still applies to all); otherwise select just this row.
    if (!selectedSet.has(obj.id)) onSelect(obj.id, 'replace')
    setMenu({ id: obj.id, x: e.clientX, y: e.clientY })
  }

  const menuItem = 'w-full text-left px-md h-[37px] flex items-center text-text-md rounded-sm transition-all'

  return (
    <>
      <div className="w-[240px] flex-shrink-0 border-l border-gray-200 bg-white flex flex-col">
        <div className="px-md py-sm border-b border-gray-200 flex items-center justify-between gap-sm">
          <span className="text-title-xs uppercase tracking-wide text-gray-500">
            Objects <span className="text-gray-400">({objects.length})</span>
          </span>
          {selectedIds.length >= 2 && (
            <button
              onClick={() => onJoin(selectedIds)}
              className="h-[24px] px-sm rounded-sm bg-primary-500 hover:bg-primary-600 text-white text-title-xs transition-all"
              title="Merge the selected parts into one"
            >
              Join {selectedIds.length}
            </button>
          )}
        </div>

        <div className="px-md py-xs text-text-xs text-gray-400 border-b border-gray-200">
          Ctrl/⌘-click to multi-select, Shift for a range
        </div>

        <div className="flex-1 overflow-y-auto p-xs flex flex-col gap-[2px]">
          {objects.map((obj) => {
            const isSelected = selectedSet.has(obj.id)
            const isEditing = obj.id === editingId
            return (
              <div
                key={obj.id}
                ref={obj.id === primaryId ? selectedRowRef : undefined}
                onClick={(e) => !isEditing && onSelect(obj.id, modeFromEvent(e))}
                onDoubleClick={() => startEdit(obj)}
                onContextMenu={(e) => openMenu(e, obj)}
                className={`group flex items-center gap-sm px-sm h-[32px] rounded-sm cursor-pointer transition-all
                  ${isSelected ? 'bg-primary-100 text-primary-800' : 'text-gray-700 hover:bg-gray-100'}
                  ${obj.hidden ? 'opacity-50' : ''}`}
                title={obj.id}
              >
                <span className={`flex-shrink-0 ${isSelected ? 'text-primary-600' : 'text-gray-400'}`}>
                  {obj.logo ? <Tag size={13} /> : <Box size={13} />}
                </span>

                {isEditing ? (
                  <input
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commitEdit}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitEdit()
                      else if (e.key === 'Escape') setEditingId(null)
                    }}
                    className="flex-1 min-w-0 h-[24px] bg-white text-gray-900 rounded-sm px-xs font-mono text-text-sm outline-none border border-primary-500"
                  />
                ) : (
                  <>
                    <span className="flex-1 min-w-0 truncate font-mono text-text-sm">{obj.id}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleHide(obj.id)
                      }}
                      className={`flex-shrink-0 hover:text-gray-900 ${
                        obj.hidden ? 'text-gray-500' : 'text-gray-400 opacity-0 group-hover:opacity-100'
                      }`}
                      title={obj.hidden ? 'Show' : 'Hide'}
                    >
                      {obj.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        startEdit(obj)
                      }}
                      className="flex-shrink-0 text-gray-400 hover:text-gray-900 opacity-0 group-hover:opacity-100"
                      title="Rename"
                    >
                      <Pencil size={12} />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Context menu — the same shell the portal's popovers use. */}
      {menu && (
        <div
          className="fixed z-50 min-w-[200px] bg-white border border-primary-500 shadow-focus rounded-md p-[4px] overflow-hidden"
          style={{ top: menu.y, left: menu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-md py-xs text-text-sm font-mono text-gray-400 truncate border-b border-gray-200 mb-[2px]">
            {menu.id}
          </div>
          {selectedIds.length >= 2 && selectedSet.has(menu.id) && (
            <button
              className={`${menuItem} text-gray-700 hover:bg-primary-100`}
              onClick={() => {
                onJoin(selectedIds)
                setMenu(null)
              }}
            >
              Join selected ({selectedIds.length})
            </button>
          )}
          <button
            className={`${menuItem} text-gray-700 hover:bg-primary-100`}
            onClick={() => {
              onToggleHide(menu.id)
              setMenu(null)
            }}
          >
            {objects.find((o) => o.id === menu.id)?.hidden ? 'Show' : 'Hide'}
          </button>
          <button
            className={`${menuItem} text-gray-700 hover:bg-primary-100`}
            onClick={() => {
              onSplit(menu.id)
              setMenu(null)
            }}
          >
            Split into parts
          </button>
          <button
            className={`${menuItem} text-gray-700 hover:bg-primary-100`}
            onClick={() => {
              onSplitByHeight(menu.id)
              setMenu(null)
            }}
          >
            Split at a height…
          </button>
          <button
            className={`${menuItem} text-gray-700 hover:bg-primary-100`}
            onClick={() => {
              const obj = objects.find((o) => o.id === menu.id)
              if (obj) startEdit(obj)
              setMenu(null)
            }}
          >
            Rename
          </button>
          <button
            className={`${menuItem} text-error-600 hover:bg-error-50`}
            onClick={() => {
              onDelete(menu.id)
              setMenu(null)
            }}
          >
            Delete
          </button>
        </div>
      )}
    </>
  )
}
